// This file is for helpers that relate to any constants within the loading module

import * as R from 'ramda';
import { parseISO } from 'date-fns/fp';
import { EvaluateFlag } from 'common/FeatureFlags/Flag';

export const STEP_ONE_HEADER = 'How do you want to select authorizations?';
export const STEP_ONE_SUBHEADER = 'Where would you like to activate authorizations?';
export const STEP_TWO_PRODUCTS = 'What products do you want to turn on at ';
export const STEP_TWO_PRODUCTS_SUPP_AUTH = 'What products do you want to turn on for ';
export const STEP_TWO_LOCATIONS_SUPP_AUTH = 'What locations do you want to turn on for ';
export const STEP_TWO_LOCATIONS = 'What locations do you want to turn on for ';
export const STEP_THREE_HEADER = 'Setting up Self Authorizations for ';

export const EXPIRATION_MESSAGE = `This authorization will expire at 11:59pm on the End Date specified and cannot be
modified after it expires.`;

export const VIEW_BY_OPTIONS = [
  { label: 'Product', value: 'product' },
  { label: 'Location', value: 'location' },
];

export const CARRIER_AUTH_BY_OPTIONS = [
  { label: 'By Carrier', value: 'carrier' },
  { label: 'By Location', value: 'location' },
];

export const SELF_AUTH_TYPE_OPTIONS = [
  { label: 'Base Products', value: 'BASE' },
  { label: 'Custom Products', value: 'CUSTOM' },
  { label: 'Supplier Directed Blends', value: 'SDB' },
];

export const ACTIVE_TAB_SELF_AUTH_MAP = {
  baseProducts: 'BASE',
  customProducts: 'CUSTOM',
  supplierDirectedBlends: 'SDB',
};

export const SUPPLIER_VIEW_BY_OPTIONS = [
  { label: 'Supplier', value: 'supplier' },
  ...VIEW_BY_OPTIONS,
];

export const MY_AUTHORIZATIONS_VIEW_BY_OPTIONS = [
  { label: 'Inventory Owner', value: 'inventory-owner' },
  { label: 'Product', value: 'product' },
  { label: 'Location', value: 'location' },
];

export const CARRIER_AUTHORIZATION_VIEW_BY_OPTIONS = [
  { label: 'Carrier', value: 'carrier' },
  { label: 'Location', value: 'location' },
];

export const NO_END_DATE = parseISO('2999-12-31T23:59:59');

export const END_DATE_FORMAT = 'M/dd/yy';

// List of all types (there's probably more)
export const AUTHORIZATION_TYPE = {
  DAILY: 'DAILY',
  LIMITED: 'LIMITED',
  MONTHLY: 'MONTHLY',
  PRODUCT: 'PRODUCT',
  SELF: 'SELF',
  SEMIMONTH: 'SEMIMONTH',
  UNLIMITED: 'UNLIMITED',
  WEEKLY: 'WEEKLY',
};

// TODO: consolidate AUTHORIZATION_TYPE and AUTHORIZATION_TYPES to use single object
export const AUTHORIZATION_TYPES = {
  DAILY: 'Daily',
  LIMITED: 'Limited',
  MONTHLY: 'Monthly',
  PRODUCT: 'Product',
  SELF: 'Self',
  SEMIMONTH: 'Semi-Monthly',
  UNLIMITED: 'Unlimited',
  WEEKLY: 'Weekly',
};

export const AUTH_TYPE = [
  {
    label: 'Unlimited',
    value: 'UNLIMITED',
  },
  {
    label: 'Limited',
    value: 'LIMITED',
  },
  {
    label: 'Product Specific',
    value: 'SPECIFIC',
  },
  {
    label: 'Monthly',
    value: 'MONTHLY',
  },
  {
    label: 'Weekly',
    value: 'WEEKLY',
  },
  {
    label: 'Daily',
    value: 'DAILY',
  },
  {
    label: 'One Load',
    value: 'ONELOAD',
  },
  {
    label: 'One Time',
    value: 'ONETIME',
  },
  {
    label: 'Semi-Monthly',
    value: 'SEMIMONTHLY',
  },
  {
    label: 'One Vol',
    value: 'ONEVOL',
  },
  {
    label: '1 Vol Daily',
    value: '1VOLDAILY',
  },
  {
    label: '1 Vol Weekly',
    value: '1VOLWEEKLY',
  },
  {
    label: '1 Vol Monthly',
    value: '1VOLMONTH',
  },
];

export const getSupplierMainAuthTypes = () =>
  AUTH_TYPE.filter(
    ({ value }) =>
      value !== '1VOLDAILY' &&
      value !== '1VOLWEEKLY' &&
      value !== '1VOLMONTH' &&
      value !== 'SEMIMONTHLY'
  );

export const getSupplierProductAuthTypes = () =>
  AUTH_TYPE.filter(({ value }) => value !== 'SEMIMONTHLY' && value !== 'ONEVOL');

// Fixed option list for the Edit Customer Authorization drawer's Authorization Type dropdown
export const CUSTOMER_AUTH_TYPE_OPTIONS = AUTH_TYPE.filter(
  ({ value }) => !['SEMIMONTHLY', 'ONEVOL', '1VOLDAILY', '1VOLWEEKLY', '1VOLMONTH'].includes(value)
);

// Selectable types (such as dropdowns)
export const SUPPLIER_AUTHORIZATION_TYPE = R.omit([AUTHORIZATION_TYPE.SELF], AUTHORIZATION_TYPE);

export const SUPPLIER_AUTHORIZATION_TYPE_OPTIONS = [
  { label: AUTHORIZATION_TYPES.UNLIMITED, value: SUPPLIER_AUTHORIZATION_TYPE.UNLIMITED },
  { label: AUTHORIZATION_TYPES.LIMITED, value: SUPPLIER_AUTHORIZATION_TYPE.LIMITED },
  { label: AUTHORIZATION_TYPES.MONTHLY, value: SUPPLIER_AUTHORIZATION_TYPE.MONTHLY },
  { label: AUTHORIZATION_TYPES.SEMIMONTH, value: SUPPLIER_AUTHORIZATION_TYPE.SEMIMONTH },
  { label: AUTHORIZATION_TYPES.WEEKLY, value: SUPPLIER_AUTHORIZATION_TYPE.WEEKLY },
  { label: AUTHORIZATION_TYPES.DAILY, value: SUPPLIER_AUTHORIZATION_TYPE.DAILY },
];

export const AUTH_SEQUENCING_ROUTES = ['location', 'product'];

// Feature flag: REACT_APP_LD_SEQUENCING_AUTH_LIST
export const isAuthSequencingUpdatesEnabled = () =>
  EvaluateFlag(process.env.REACT_APP_LD_SEQUENCING_AUTH_LIST);

export const CARRIER_AUTH_ROUTES = ['carrier', 'location'];

export const BASE_AND_CUSTOM_TABS = [
  { id: 'baseProducts', name: 'Base Products', shortName: 'Base' },
  { id: 'customProducts', name: 'Custom Products', shortName: 'Custom' },
];

export const SELF_AUTH_TABS = [
  ...BASE_AND_CUSTOM_TABS,
  { id: 'supplierDirectedBlends', name: 'Supplier Directed Blends', shortName: 'SDB' },
];

export const AUTH_SEQUENCING_ADD_AUTH =
  'Add an authorization from the list below to enable loading.';
export const AUTH_SEQUENCING_ALL_DONE = 'All available authorizations have been sequenced';

export const ANY_VALUE = 'Any';
export const ANY_OPTION = { label: 'Any', value: ANY_VALUE };

export const AUTH_SEQUENCING_FILTERS = {
  allowed: { label: 'Loading Allowed', value: 'loadingAllowed' },
  notAllowed: { label: 'Loading Not Allowed', value: 'loadingNotAllowed' },
};

export const AUTH_SEQUENCING_LOADING_ALLOWED_FILTER_OPTIONS = [
  ANY_OPTION,
  AUTH_SEQUENCING_FILTERS.allowed,
  AUTH_SEQUENCING_FILTERS.notAllowed,
];

export const CARRIER_AUTH_FILTERS = {
  allowed: { label: 'Limit Customers Allowed', value: 'customerLimit' },
  notAllowed: { label: 'Do not Limit Customers Allowed', value: 'noCustomerLimit' },
};

export const MOCK_CUSTOMER_LIST = [
  {
    CustomerIdName: 1046156,
    CustomerName: '641300 BENCHMARK RESOURCES INC - VARIOUS NE',
  },
  {
    CustomerIdName: 1046136,
    CustomerName: '487300 BENCHMARK RESOURCES INC - VARIOUS NE',
  },
  {
    CustomerIdName: 753821,
    CustomerName: '848010 BENCHMARK RESOURCES- VAR NE',
  },
  {
    CustomerIdName: 1039916,
    CustomerName: '850882 BOSSELMAN ENERGY INC - VAR NE',
  },
];

export const MOCK_UNAUTH_LIST = [
  {
    locationId: 163868,
    nameLong: '191 EL PASO, TX MPL',
    nameShort: '191 EL PASO MPL',
  },
  {
    locationId: 202,
    nameLong: '202 IOWA CITY',
    nameShort: '202 IOWA CITY',
  },
  {
    locationId: 207,
    nameLong: '207 MASON CITY',
    nameShort: '207 MASON CITY',
  },
  {
    locationId: 208,
    nameLong: '208 MANKATO',
    nameShort: '208 MANKATO',
  },
  {
    locationId: 383,
    nameLong: '383 ROSEVILLE-FLINT HILLS RESOURCES',
    nameShort: '383 ROSEVILE FHR',
  },
];

// putting this here so lint disabling only has to happen in one place
// each item that has a media query needs the same keys, so there is some duplication in top/width
export const sidebarStyles = {
  bgcolor: 'surface.white',
  border: 1,
  borderBottom: 0,
  borderColor: 'graphic.light',
  borderLeft: 0,
  borderTop: 0,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  pb: 2,
  position: 'fixed',
  // eslint-disable-next-line
  top: { xs: 56, sm: 64, md: 64, lg: 48 },
  // eslint-disable-next-line
  width: { xs: 1, lg: 300 },
};

export const cardsPanelContainerStyles = {
  bgcolor: 'surface.light',
  // eslint-disable-next-line
  mt: { xs: 0, md: 6 },
  // eslint-disable-next-line
  pl: { xs: 1, md: 37.5 },
  // eslint-disable-next-line
  pr: { xs: 1, md: 0 },
  // eslint-disable-next-line
  pb: { xs: 8, md: 0 },
};

// contains all the styles for the cards list, need to do padding bottom based on ternary if list tabs are shown
export const cardsListStyles = {
  display: 'flex',
  flexWrap: 'wrap',
  // eslint-disable-next-line
  ml: { xs: 0, lg: 2 },
  // eslint-disable-next-line
  mt: { xs: 0, lg: -1 },
};

export const stateList = {
  ALABAMA: 'AL',
  ALASKA: 'AK',
  ARIZONA: 'AZ',
  ARKANSAS: 'AR',
  CALIFORNIA: 'CA',
  COLORADO: 'CO',
  CONNECTICUT: 'CT',
  DELAWARE: 'DE',
  FLORIDA: 'FL',
  GEORGIA: 'GA',
  HAWAII: 'HI',
  IDAHO: 'ID',
  ILLINOIS: 'IL',
  INDIANA: 'IN',
  IOWA: 'IA',
  KANSAS: 'KS',
  KENTUCKY: 'KY',
  LOUISIANA: 'LA',
  MAINE: 'ME',
  MARYLAND: 'MD',
  MASSACHUSETTS: 'MA',
  MICHIGAN: 'MI',
  MINNESOTA: 'MN',
  MISSISSIPPI: 'MS',
  MISSOURI: 'MO',
  MONTANA: 'MT',
  NEBRASKA: 'NE',
  NEVADA: 'NV',
  'NEW HAMPSHIRE': 'NH',
  'NEW JERSEY': 'NJ',
  'NEW MEXICO': 'NM',
  'NEW YORK': 'NY',
  'NORTH CAROLINA': 'NC',
  'NORTH DAKOTA': 'ND',
  OHIO: 'OH',
  OKLAHOMA: 'OK',
  OREGON: 'OR',
  PENNSYLVANIA: 'PA',
  'RHODE ISLAND': 'RI',
  'SOUTH CAROLINA': 'SC',
  'SOUTH DAKOTA': 'SD',
  TENNESSEE: 'TN',
  TEXAS: 'TX',
  UTAH: 'UT',
  VERMONT: 'VT',
  VIRGINIA: 'VA',
  WASHINGTON: 'WA',
  'WEST VIRGINIA': 'WV',
  WISCONSIN: 'WI',
  WYOMING: 'WY',
};

export const selectorsListStyles = () => ({
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  height: '100%',
  overflowY: 'scroll',
  scrollbarWidth: 'none',
});
